import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { authService, userService } from '../services/firebase';
import { Timestamp } from 'firebase/firestore';

const FinishRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    password: '',
    confirmPassword: '',
  });
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Check if the URL is a sign-in link
    if (authService.isSignInWithEmailLink(window.location.href)) {
      // Get the email from localStorage
      const storedEmail = window.localStorage.getItem('emailForSignIn');
      if (!storedEmail) {
        setError('Kayıt oturumu bulunamadı veya süresi dolmuş. Lütfen kayıt işlemini baştan başlatın.');
        return;
      }
      setEmail(storedEmail);
    } else {
      setError('Geçersiz doğrulama linki. Lütfen kayıt işlemini baştan başlatın.');
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFinishRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.password) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Şifreler uyuşmuyor.');
      return;
    }
    if (form.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (!email) {
      setError('E-posta adresi bulunamadı. Lütfen kayıt işlemini baştan başlatın.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Sign in the user with the email link
      const userCredential = await authService.signInWithEmailLink(email, window.location.href);
      const user = userCredential.user;

      // 2. Update the password
      await authService.updatePassword(user, form.password);

      // 3. Update the user's profile (display name)
      await authService.updateUserProfile(user, { displayName: form.name });

      // 4. Create the user profile in Firestore
      await userService.createUserWithUID(user.uid, {
        displayName: form.name,
        email: email,
        phoneNumber: '',
        role: 'user',
        bio: '',
        avatar: '',
        isVerified: true, // Email is verified by this process
        lastLoginAt: Timestamp.now(),
      });

      // 5. Clean up the email from localStorage
      window.localStorage.removeItem('emailForSignIn');

      setSuccess('Kayıt başarıyla tamamlandı! Profilinize yönlendiriliyorsunuz...');
      
      // Redirect to profile page after a short delay
      setTimeout(() => {
        navigate('/profile');
      }, 2000);

    } catch (err: any) {
      console.error('Error finishing registration:', err);
      setError(err.message || 'Kayıt tamamlanırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  if (!email && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Kaydı Tamamla - HatTravel</title>
      </Helmet>
      <Box sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Card sx={{ background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: 4, p: 4 }}>
            <CardContent>
              <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 200, textAlign: 'center', background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 2 }}>
                Kaydı Tamamla
              </Typography>
              <Typography variant="h6" color="#7f8c8d" sx={{ textAlign: 'center', mb: 4, fontWeight: 300 }}>
                E-posta adresiniz doğrulandı: <strong>{email}</strong>. Şimdi son adımları tamamlayın.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
              {success ? (
                <Box sx={{ textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                  <Alert severity="success">{success}</Alert>
                </Box>
              ) : (
                <form onSubmit={handleFinishRegistration}>
                  <TextField
                    fullWidth
                    label="Ad Soyad"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                  />
                  <TextField
                    fullWidth
                    label="Şifre"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(p => !p)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Şifre Tekrar"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                  />
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    sx={{ borderRadius: 3, py: 1.5, backgroundColor: '#005BBD', '&:hover': { backgroundColor: '#004A9F' } }}
                  >
                    {loading ? 'Kaydediliyor...' : 'Kaydı Tamamla ve Giriş Yap'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default FinishRegistrationPage; 